import type {
  IndodaxDepthResponse,
  IndodaxHistoryBar,
  IndodaxKlineResponse,
  IndodaxPairsResponse,
  IndodaxTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://indodax.com';

/**
 * Indodax REST API endpoints.
 * @see https://github.com/btcid/indodax-official-api-docs
 */
export const indodaxRestEndpoints = {
  /** Get server time */
  serverTime: `${BASE_URL}/api/server_time`,
  /** Get trading pair info */
  pairs: `${BASE_URL}/api/pairs`,
  /** Get price increments (tick sizes) for all pairs */
  priceIncrements: `${BASE_URL}/api/price_increments`,
  /** Get summaries (includes tickers map) */
  summaries: `${BASE_URL}/api/summaries`,
  /** Get ticker for a symbol */
  ticker: (symbol: string) => `${BASE_URL}/api/ticker/${symbol}`,
  /** Get all tickers */
  tickerAll: `${BASE_URL}/api/ticker_all`,
  /** Get orderbook depth */
  depth: (pair: string) => `${BASE_URL}/api/depth/${pair}`,
  /** Get recent trades */
  trades: (pair: string) => `${BASE_URL}/api/trades/${pair}`,
  /** Get OHLC data via TradingView UDF endpoint */
  klines: `${BASE_URL}/tradingview/history_v2`,
} as const;

// ========================================
// Symbol Helpers
// ========================================

/**
 * Formats base and quote currencies into Indodax symbol format.
 * Indodax uses lowercase concatenated format: btcidr (no separator)
 *
 * @example
 * formatIndodaxSymbol('BTC', 'IDR') // 'btcidr'
 */
export const formatIndodaxSymbol = (base: string, quote: string): string => {
  return `${base.toLowerCase()}${quote.toLowerCase()}`;
};

/**
 * Formats base and quote currencies into Indodax ticker ID format.
 * Ticker ID uses underscore separator: btc_idr
 *
 * @example
 * formatIndodaxTickerId('BTC', 'IDR') // 'btc_idr'
 */
export const formatIndodaxTickerId = (base: string, quote: string): string => {
  return `${base.toLowerCase()}_${quote.toLowerCase()}`;
};

/**
 * Parses an Indodax symbol into base and quote currencies.
 * Note: This is tricky since Indodax symbols have no separator (btcidr).
 * Assumes IDR is the quote currency for most pairs.
 *
 * @example
 * parseIndodaxSymbol('btcidr') // { base: 'btc', quote: 'idr' }
 */
export const parseIndodaxSymbol = (symbol: string): { base: string; quote: string } => {
  const lower = symbol.toLowerCase();
  // Most Indodax pairs are quoted in IDR
  if (lower.endsWith('idr')) {
    return { base: lower.slice(0, -3), quote: 'idr' };
  }
  // Fallback: assume last 4 chars are quote (usdt, btc, etc.)
  if (lower.length > 4) {
    return { base: lower.slice(0, -4), quote: lower.slice(-4) };
  }
  return { base: lower, quote: '' };
};

// ========================================
// Ticker Types (kept locally for backward compatibility)
// ========================================

export interface IndodaxTickerData {
  high: string;
  low: string;
  last: string;
  buy: string;
  sell: string;
  server_time: number;
  [volumeKey: string]: string | number;
}

export interface IndodaxTickerResponse {
  ticker: IndodaxTickerData;
}

export interface IndodaxTickerAllResponse {
  tickers: {
    [symbol: string]: IndodaxTickerData;
  };
}

export interface IndodaxServerTimeResponse {
  timezone: string;
  server_time: number;
}

export interface IndodaxPriceIncrementsResponse {
  increments: Record<string, string>;
}

export interface IndodaxSummariesResponse {
  tickers: Record<string, IndodaxTickerData & { name?: string }>;
  prices_24h?: Record<string, string>;
  prices_7d?: Record<string, string>;
}

// ========================================
// REST API Functions
// ========================================

/**
 * Fetches all available trading pairs.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-Rest-API.md#pairs
 */
export const getIndodaxPairs = async (
  options?: RestRequestOptions,
): Promise<IndodaxPairsResponse> => {
  const response = await get<IndodaxPairsResponse>({
    url: indodaxRestEndpoints.pairs,
    options,
  });

  return response.data;
};

/**
 * Fetches Indodax server time.
 * Note: Indodax docs state time fields are in milliseconds.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-RestAPI.md#server-time
 */
export const getIndodaxServerTime = async (
  options?: RestRequestOptions,
): Promise<IndodaxServerTimeResponse> => {
  const response = await get<IndodaxServerTimeResponse>({
    url: indodaxRestEndpoints.serverTime,
    options,
  });

  return response.data;
};

/**
 * Fetches price increments (tick sizes) for all pairs.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-RestAPI.md#price-increments
 */
export const getIndodaxPriceIncrements = async (
  options?: RestRequestOptions,
): Promise<IndodaxPriceIncrementsResponse> => {
  const response = await get<IndodaxPriceIncrementsResponse>({
    url: indodaxRestEndpoints.priceIncrements,
    options,
  });

  return response.data;
};

/**
 * Fetches summaries (includes tickers map and prices_24h/prices_7d).
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-RestAPI.md#summaries
 */
export const getIndodaxSummaries = async (
  options?: RestRequestOptions,
): Promise<IndodaxSummariesResponse> => {
  const response = await get<IndodaxSummariesResponse>({
    url: indodaxRestEndpoints.summaries,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * Note: Symbol should be lowercase with underscore (e.g., btc_idr)
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-Rest-API.md#ticker
 */
export const getIndodaxTicker = async (
  symbol: string,
  options?: RestRequestOptions,
): Promise<IndodaxTickerResponse> => {
  const response = await get<IndodaxTickerResponse>({
    url: indodaxRestEndpoints.ticker(symbol),
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour statistics for all trading pairs.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-Rest-API.md#ticker-all
 */
export const getIndodaxTickers = async (
  options?: RestRequestOptions,
): Promise<IndodaxTickerAllResponse> => {
  const response = await get<IndodaxTickerAllResponse>({
    url: indodaxRestEndpoints.tickerAll,
    options,
  });

  return response.data;
};

/**
 * Request parameters for Indodax depth endpoint.
 */
export interface IndodaxDepthRequest {
  /** Trading pair (e.g., btcidr - no separator) */
  pair: string;
}

/**
 * Fetches orderbook depth for a symbol.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-Rest-API.md#depth
 */
export const getIndodaxDepth = async (
  params: IndodaxDepthRequest,
  options?: RestRequestOptions,
): Promise<IndodaxDepthResponse> => {
  const response = await get<IndodaxDepthResponse>({
    url: indodaxRestEndpoints.depth(params.pair),
    options,
  });

  return response.data;
};

/**
 * Request parameters for Indodax trades endpoint.
 */
export interface IndodaxTradesRequest {
  /** Trading pair (e.g., btcidr - no separator) */
  pair: string;
}

/**
 * Fetches recent trades for a symbol.
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-Rest-API.md#trades
 */
export const getIndodaxTrades = async (
  params: IndodaxTradesRequest,
  options?: RestRequestOptions,
): Promise<IndodaxTradesResponse> => {
  const response = await get<IndodaxTradesResponse>({
    url: indodaxRestEndpoints.trades(params.pair),
    options,
  });

  return response.data;
};

/**
 * Request parameters for Indodax klines endpoint (TradingView UDF).
 */
export interface IndodaxKlinesRequest {
  /** Trading pair symbol (e.g., btcidr - no separator) */
  symbol: string;
  /** Resolution/timeframe (e.g., '1', '15', '60', 'D', 'W') */
  resolution: string;
  /** Start timestamp (Unix seconds) */
  from: number;
  /** End timestamp (Unix seconds) */
  to: number;
}

const normalizeIndodaxHistoryTf = (value: string): string => {
  const upper = value.toUpperCase();
  // Backward-compatible aliases
  if (upper === 'D') return '1D';
  if (upper === 'W') return '1W';
  return upper;
};

const normalizeIndodaxHistorySymbol = (value: string): string =>
  value.replace(/_/g, '').toUpperCase();

const isIndodaxHistoryBars = (data: unknown): data is IndodaxHistoryBar[] => {
  if (!Array.isArray(data)) {
    return false;
  }
  const first = data[0] as any;
  return (
    first &&
    typeof first === 'object' &&
    typeof first.Time === 'number' &&
    typeof first.Open === 'number'
  );
};

const normalizeIndodaxHistoryResponse = (data: unknown): IndodaxKlineResponse => {
  if (!isIndodaxHistoryBars(data)) {
    return data as IndodaxKlineResponse;
  }

  if (data.length === 0) {
    return { s: 'no_data', t: [], o: [], h: [], l: [], c: [], v: [] };
  }

  const t: number[] = [];
  const o: number[] = [];
  const h: number[] = [];
  const l: number[] = [];
  const c: number[] = [];
  const v: number[] = [];

  data.forEach(bar => {
    t.push(bar.Time);
    o.push(bar.Open);
    h.push(bar.High);
    l.push(bar.Low);
    c.push(bar.Close);
    v.push(Number(bar.Volume));
  });

  return { s: 'ok', t, o, h, l, c, v };
};

/**
 * Fetches OHLC (kline/candlestick) data via TradingView UDF endpoint.
 * Official Indodax docs describe:
 * - Request: /tradingview/history_v2?from=...&to=...&tf=...&symbol=BTCIDR
 * - Response: array of objects { Time, Open, High, Low, Close, Volume }
 * This SDK normalizes the response into a TradingView-UDF-like shape used by the app.
 *
 * NOTE:
 * - Request `from` / `to` are Unix timestamps in seconds (per docs)
 * - Response `Time` is also seconds in the examples
 *
 * @see https://github.com/btcid/indodax-official-api-docs/blob/master/Public-RestAPI.md#ohlc-history
 */
export const getIndodaxKlines = async (
  params: IndodaxKlinesRequest,
  options?: RestRequestOptions,
): Promise<IndodaxKlineResponse> => {
  const queryParams = {
    from: params.from,
    to: params.to,
    tf: normalizeIndodaxHistoryTf(params.resolution),
    symbol: normalizeIndodaxHistorySymbol(params.symbol),
  };

  const fullUrl = `${indodaxRestEndpoints.klines}?from=${queryParams.from}&to=${queryParams.to}&tf=${queryParams.tf}&symbol=${queryParams.symbol}`;
  console.log(`[Indodax SDK] GET ${fullUrl}`);

  try {
    const response = await get<unknown, typeof queryParams>({
      url: indodaxRestEndpoints.klines,
      params: queryParams,
      options,
    });

    const rawData = response.data;
    const normalized = normalizeIndodaxHistoryResponse(rawData);

    console.log(
      `[Indodax SDK] Response: rawType=${typeof rawData}, isArray=${Array.isArray(rawData)}, ` +
        `rawLength=${Array.isArray(rawData) ? rawData.length : 'N/A'}, ` +
        `normalizedStatus=${normalized.s}, normalizedBars=${normalized.t?.length ?? 0}`,
    );

    return normalized;
  } catch (error) {
    console.error('[Indodax SDK] Request failed:', error);
    throw error;
  }
};
